import {Dispatch, SetStateAction} from 'react'

import {Dinero} from 'dinero.js'
import {create} from 'zustand'

import {
  CreateAccountMutationVariables,
  Member,
  SetupMenopauseProfileMutationVariables,
  SetupPregnancyPostpartumProfileMutationVariables,
  SetupProfileMutationVariables,
  User,
} from '@progyny/generated/graphql'

// ── Exported types (previously lived in contexts/User) ────────────────────────

export type OnBoardingData = CreateAccountMutationVariables &
  SetupProfileMutationVariables &
  SetupMenopauseProfileMutationVariables &
  SetupPregnancyPostpartumProfileMutationVariables & {
    isAspiring?: boolean
    isEligible?: boolean
  }

export type MeUser = Omit<User, 'member' | 'createdAt'> & {
  member: Omit<Member, 'memberStatus' | 'createdAt'>
}

/** The public-facing user state shape — what components read from the store.
 *  Replaces the old MeContextType (from the deleted React context). */
export type UserState = {
  user?: MeUser
  companyHasBudget: boolean
  companyName: string | undefined
  countryCode: string | undefined
  error: any
  evaluateProfileForProductLine: (productLine: string) => boolean
  getMyLocale: () => string
  hasAcceptedTerms: boolean
  hasReimbursementRequests: boolean
  hasStartedLegacyOnboarding: boolean
  isAccountCreated: boolean
  isAuthorized: boolean
  isEmailConfirmed: boolean
  isHelpOverlayOpen: boolean
  isInConservativeCountry: () => boolean
  isLoggedIn: boolean
  isProfileSetup: boolean
  isSsoLogin: boolean
  isTestCompany: boolean
  journeyCommunicationEmail: string | undefined
  loading: boolean
  logout: () => void
  mainEmail: string | undefined
  onboardingData: OnBoardingData
  privateEmailChangePending: boolean
  setMyLocale: (dinero: Dinero) => Dinero
  setOnboardingData: Dispatch<SetStateAction<OnBoardingData>>
  signupEmployee: Function
  toggleHelpOverlay: Dispatch<SetStateAction<boolean>>
  refetch: Function
}

// ── Internal store state ──────────────────────────────────────────────────────

// The store holds EVERY field from UserState directly (raw data AND derived
// booleans). Storing derived values (isLoggedIn, isAuthorized, …) instead of
// recomputing them from `user` means tests can provide e.g. isLoggedIn:true
// without supplying a full user object.
export interface MeStoreState {
  // ── Apollo-derived raw data ────────────────────────────────────────────────
  user: MeUser | undefined // undefined === not logged in; aligns with UserState user?: MeUser
  loading: boolean
  error: any

  // ── Local mutable state ────────────────────────────────────────────────────
  onboardingData: OnBoardingData
  isHelpOverlayOpen: boolean

  // ── Stable function references populated by UserProvider ──────────────────
  refetch: Function
  signupEmployee: Function
  evaluateProfileForProductLine: (productLine: string) => boolean
  getMyLocale: () => string
  setMyLocale: (dinero: Dinero) => Dinero
  isInConservativeCountry: () => boolean
  logout: () => void

  // ── Derived / computed values (stored directly, never recomputed here) ─────
  isLoggedIn: boolean
  isEmailConfirmed: boolean
  hasAcceptedTerms: boolean
  isAccountCreated: boolean
  isProfileSetup: boolean
  isAuthorized: boolean
  isSsoLogin: boolean
  privateEmailChangePending: boolean
  companyHasBudget: boolean
  isTestCompany: boolean
  mainEmail: string | undefined
  journeyCommunicationEmail: string | undefined
  countryCode: string | undefined
  companyName: string | undefined
  hasReimbursementRequests: boolean
  hasStartedLegacyOnboarding: boolean

  // ── Store actions ──────────────────────────────────────────────────────────
  /** Bulk-patch called by UserProvider on each Apollo data change. */
  setMeData: (patch: Partial<MeStoreState>) => void
  setOnboardingData: Dispatch<SetStateAction<OnBoardingData>>
  toggleHelpOverlay: Dispatch<SetStateAction<boolean>>
}

const noop = () => {}
const identity = (d: Dinero) => d

export const useMeStore = create<MeStoreState>()((set, get) => ({
  // ── Initial (pre-Apollo) values ────────────────────────────────────────────
  user: undefined,
  loading: true,
  error: undefined,
  onboardingData: {},
  isHelpOverlayOpen: false,
  refetch: noop,
  signupEmployee: noop,
  evaluateProfileForProductLine: () => false,
  getMyLocale: () => 'en-US',
  setMyLocale: identity,
  isInConservativeCountry: () => false,
  logout: noop,
  isLoggedIn: false,
  isEmailConfirmed: false,
  hasAcceptedTerms: false,
  isAccountCreated: false,
  isProfileSetup: false,
  isAuthorized: false,
  isSsoLogin: false,
  privateEmailChangePending: false,
  companyHasBudget: true,
  isTestCompany: false,
  mainEmail: undefined,
  journeyCommunicationEmail: undefined,
  countryCode: undefined,
  companyName: undefined,
  hasReimbursementRequests: false,
  hasStartedLegacyOnboarding: false,

  // ── Actions ────────────────────────────────────────────────────────────────
  setMeData: (patch) => set(patch),

  setOnboardingData: (value) => {
    const current = get().onboardingData
    const next = typeof value === 'function' ? value(current) : value
    set({onboardingData: next})
  },

  toggleHelpOverlay: (value) => {
    const current = get().isHelpOverlayOpen
    const next = typeof value === 'function' ? value(current) : value
    set({isHelpOverlayOpen: next})
  },
}))

// ── Test helpers ──────────────────────────────────────────────────────────────

/** In tests, seed the store with a partial fixture without full type checking.
 *  Use resetMeStore() in afterEach for cleanup. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setMeStore = (patch: any) => useMeStore.setState(patch)

export const resetMeStore = () =>
  useMeStore.setState({
    user: undefined,
    loading: true,
    error: undefined,
    onboardingData: {},
    isHelpOverlayOpen: false,
    refetch: noop,
    signupEmployee: noop,
    evaluateProfileForProductLine: () => false,
    getMyLocale: () => 'en-US',
    setMyLocale: identity,
    isInConservativeCountry: () => false,
    logout: noop,
    isLoggedIn: false,
    isEmailConfirmed: false,
    hasAcceptedTerms: false,
    isAccountCreated: false,
    isProfileSetup: false,
    isAuthorized: false,
    isSsoLogin: false,
    privateEmailChangePending: false,
    companyHasBudget: true,
    isTestCompany: false,
    mainEmail: undefined,
    journeyCommunicationEmail: undefined,
    countryCode: undefined,
    companyName: undefined,
    hasReimbursementRequests: false,
    hasStartedLegacyOnboarding: false,
  })
